import { painAreas } from "../services";
import { TriggerObject, TSurveyQuestionTrigger, TSurveySectionTriggered } from "../types/type";

export const PAIN_DURATION_QUESTION_ID = "45cec590-843c-481d-a89a-f96a6a20da73";

export type PartialAnswerMap = Record<string, unknown>;

export type TSurveyAnswerData = {
  completion_timestamp?: number | null;
  partial_answer?: PartialAnswerMap | null;
};

const normalizePartialAnswer = (value: unknown): TriggerObject => {
  return (typeof value === "number" ? String(value) : value) as TriggerObject;
};

const hasAnswer = (answer: unknown): boolean => {
  if (answer === undefined || answer === null || answer === "") {
    return false;
  }
  if (Array.isArray(answer)) {
    return answer.length > 0;
  }
  return true;
};

const applyPartialAnswerToQuestion = (question: TSurveyQuestionTrigger, partialAnswers: PartialAnswerMap): TSurveyQuestionTrigger => {
  const partialAnswer = partialAnswers[question.Uuid];
  const mergedQuestion: TSurveyQuestionTrigger = { ...question };

  if (hasAnswer(partialAnswer)) {
    mergedQuestion.Answer = normalizePartialAnswer(partialAnswer);
  }

  if (question.TriggeredQuestions?.length) {
    mergedQuestion.TriggeredQuestions = question.TriggeredQuestions.map(child => applyPartialAnswerToQuestion(child, partialAnswers));
  }

  return mergedQuestion;
};

export const applyPartialAnswers = (
  sections: TSurveySectionTriggered[] | undefined,
  partialAnswers: PartialAnswerMap | undefined | null,
): TSurveySectionTriggered[] => {
  if (!sections?.length) {
    return [];
  }
  if (!partialAnswers || !Object.keys(partialAnswers).length) {
    return sections;
  }
  return sections.map(section => ({
    ...section,
    Questions: (section.Questions || []).map(question => applyPartialAnswerToQuestion(question, partialAnswers)),
  }));
};

/**
 * @param painAreaId
 * @returns
 */
export const getSurveyTypeByPainArea = async (painAreaId: string): Promise<string> => {
  try {
    const result = await painAreas.get();
    if (result.ok && result.data) {
      const painArea = result.data.find(area => area.id === painAreaId);
      return painArea?.survey_type || "intake-v2";
    }
    return "intake-v2";
  } catch (error) {
    console.error("Error fetching survey type from pain-areas:", error);
    return "intake-v2";
  }
};

/**
 * @param surveyAnswerData - The survey answer data from the API
 * @returns true if the answer is "No" (indicating less than 6 weeks of pain) AND completion_timestamp is null
 */
export const hasLessThanSixWeeksPain = (surveyAnswerData: TSurveyAnswerData | null | undefined): boolean => {
  if (!surveyAnswerData) {
    return false;
  }

  if (surveyAnswerData.completion_timestamp !== null) {
    return false;
  }

  if (surveyAnswerData.partial_answer && surveyAnswerData.partial_answer[PAIN_DURATION_QUESTION_ID]) {
    const answer = surveyAnswerData.partial_answer[PAIN_DURATION_QUESTION_ID];
    return answer === "No";
  }

  return false;
};

/**
 * @param surveyAnswerData - The survey answer data from the API
 * @returns true if the no-fit component should be shown
 */
export const shouldShowNoFitComponent = (surveyAnswerData: TSurveyAnswerData | null | undefined): boolean => {
  return hasLessThanSixWeeksPain(surveyAnswerData);
};
